import { useQuery } from '@apollo/client';
import { Popover, Transition } from '@headlessui/react';
import {
  ArrowUUpLeft,
  CalendarBlank,
  CalendarX,
  DotsThree,
  FloppyDisk,
  PlayCircle,
  StopCircle,
  TrashSimple,
} from '@phosphor-icons/react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import clsx from 'clsx';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { EditableTitle } from 'src/components/EditableTitle';
import { Heading } from 'src/components/Heading';
import { Input } from 'src/components/Input';
import { Modal } from 'src/components/Modal';
import { Tab } from 'src/components/Tab';
import { Tooltip as TooltipComponent } from 'src/components/Tooltip';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import {
  GET_WORKFLOW_STATS,
  GetWorkflowStatsQueryData,
  GetWorkflowStatsQueryVariables,
} from 'src/graphql/queries';
import { withIntegrationApp } from 'src/hocs/withIntegrationApp';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { useCampaigns } from 'src/hooks/useCampaigns';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import {
  PaginatedResponse,
  WorkflowExecutionThreadStatus,
  WorkflowExecutionThreadWithComputedStatus,
  WorkflowStatus,
} from 'src/interfaces/workflows.interface';
import { generateReferenceIdForDuplicate } from 'src/utils/referenceId';
import { dateToIsoString, isoStringToDate } from 'src/utils/timeZoneUtils';
import {
  useWizard,
  WizardProvider,
} from '../../components/wizard/WizardContext';
import { ActivityContent } from './components/ActivityContent';
import {
  CampaignAnalyticsSection,
  CampaignMetricsSkeleton,
  ChartContainerSkeleton,
} from './components/CampaignAnalyticsSection';
import { CampaignBadge, CampaignBadgeStatus } from './components/CampaignBadge';
import CommunicationContent from './components/CommunicationContent';
import ContactsContent from './components/ContactsContent';
import SettingsContent from './components/SettingsContent';
import { CampaignOverviewTab } from './types';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend,
);

// Types
type CampaignMetrics = {
  totalContacts: number;
  queued: number;
  active: number;
  errored: number;
  exited: number;
};

// Skeleton Component
const CampaignOverviewSkeleton = () => (
  <>
    <Heading
      className="border-b-0"
      title={
        <div className="flex items-start gap-2">
          <div className="flex flex-col gap-2">
            <Skeleton height={32} width={200} />
            <Skeleton height={20} width={300} />
          </div>
          <Skeleton height={24} width={80} className="ml-4" />
        </div>
      }
    >
      <div className="flex gap-2 mt-3">
        <Skeleton height={32} width={32} />
        <Skeleton height={32} width={120} />
        <Skeleton height={32} width={100} />
      </div>
    </Heading>
    <ContentWrapper className="p-0 h-auto">
      <div className="p-4 space-y-6">
        <CampaignMetricsSkeleton />
        <ChartContainerSkeleton />
      </div>
    </ContentWrapper>
  </>
);

// Main Component
const CampaignOverview = ({
  campaignReferenceId,
}: {
  campaignReferenceId: string;
}) => {
  // Hooks
  const navigate = useNavigate();
  const notification = useNotification();
  const { get, post } = useAuthedRequest();
  const campaignsService = useCampaigns();
  const { environment } = useEnvironment();
  const environmentId = environment?.envId;
  const {
    saveCampaignAutomation,
    activateCampaign,
    deactivateCampaign,
    setCampaignAutomation,
    campaignAutomation,
    isInitializing,
  } = useWizard();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTabParam = searchParams.get('tab');

  // Refs
  const titleInputRef = useRef<HTMLInputElement>(null);

  // State
  const [startDateDraft, setStartDateDraft] = useState('');
  const [campaignStatus, setCampaignStatus] = useState<CampaignBadgeStatus>(
    CampaignBadgeStatus.DRAFT,
  );
  const [campaignMetrics, setCampaignMetrics] = useState<CampaignMetrics>({
    totalContacts: 0,
    queued: 0,
    active: 0,
    errored: 0,
    exited: 0,
  });

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [savingNameAndDescription, setSavingNameAndDescription] =
    useState(false);

  // UI states
  const [isEditingCampaignDetails, setIsEditingCampaignDetails] =
    useState(false);
  const [isMoreOptionsDropdownOpen, setIsMoreOptionsDropdownOpen] =
    useState(false);
  const [showScheduleInput, setShowScheduleInput] = useState(false);
  const [deleteCampaignModalOpen, setDeleteCampaignModalOpen] = useState(false);
  const [canStartCampaign, setCanStartCampaign] = useState(false);

  // GraphQL Query
  const { data: workflowStatsData, loading: workflowStatsLoading } = useQuery<
    GetWorkflowStatsQueryData,
    GetWorkflowStatsQueryVariables
  >(GET_WORKFLOW_STATS, {
    variables: {
      environmentId: environmentId!,
      workflowReferenceIds: [campaignReferenceId!],
    },
    skip: !campaignReferenceId || !environmentId,
  });

  // Derived state
  const isInactive =
    campaignStatus === CampaignBadgeStatus.STOPPED ||
    campaignStatus === CampaignBadgeStatus.DRAFT;
  const canSeeScheduleInput = isInactive || showScheduleInput;

  // Event handlers
  const handleNameChange = (name: string) => {
    setCampaignAutomation((prev) => ({
      ...prev,
      name,
    }));
  };
  const handleDescriptionChange = (description: string) => {
    setCampaignAutomation((prev) => ({
      ...prev,
      description,
    }));
  };

  const handleMoreOptionsClick = (item: string) => {
    setIsMoreOptionsDropdownOpen(false);

    switch (item) {
      case 'Export Calls CSV':
        navigate(`/calls?workflow_reference_id=${campaignReferenceId}`);
        break;
      case 'Duplicate Campaign':
        handleDuplicateCampaign(campaignReferenceId);
        break;
    }
  };

  const handleDuplicateCampaign = async (referenceId?: string) => {
    if (!referenceId) return;
    try {
      setLoading(true);
      const fetchedWorkflow = await get(`/workflows/${referenceId}`);
      if (!fetchedWorkflow) {
        notification.error('Failed to duplicate campaign: campaign not found');
        return;
      }
      const duplicatedWorkflow = await post(`/workflows`, {
        ...fetchedWorkflow,
        referenceId: generateReferenceIdForDuplicate(
          fetchedWorkflow.referenceId,
        ),
        version: 1,
        id: undefined,
        createdAt: undefined,
        updatedAt: undefined,
      });
      if (!duplicatedWorkflow) {
        notification.error('Failed to duplicate campaign');
        return;
      }
      navigate('/beta/campaigns');
      notification.success('Successfully duplicated campaign');
    } catch (error) {
      notification.error('Failed to duplicate campaign');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const syncCampaignStatusFromAutomation = useCallback(() => {
    const startDate = campaignAutomation.campaignStartDate;
    if (!startDate || startDate === '') {
      setCampaignStatus(CampaignBadgeStatus.DRAFT);
      return;
    }

    switch (campaignAutomation.status) {
      case WorkflowStatus.WORKFLOW_ACTIVE: {
        const startDateTz = campaignAutomation.campaignStartDateTz;
        const startDateIso = startDateTz
          ? dateToIsoString(startDate, startDateTz)
          : undefined;
        const now = new Date();

        if (startDateIso && new Date(startDateIso) > now) {
          setCampaignStatus(CampaignBadgeStatus.SCHEDULED);
        } else {
          setCampaignStatus(CampaignBadgeStatus.RUNNING);
        }
        break;
      }
      case WorkflowStatus.WORKFLOW_PAUSED:
        setCampaignStatus(CampaignBadgeStatus.STOPPED);
        break;
      default:
        // Keep current UI state for unexpected statuses (e.g. archived)
        break;
    }
  }, [
    campaignAutomation.campaignStartDate,
    campaignAutomation.campaignStartDateTz,
    campaignAutomation.status,
  ]);

  // API calls
  const saveNameAndDescription = async () => {
    if (isInitializing) return;

    setSavingNameAndDescription(true);
    try {
      const updated = await saveCampaignAutomation();
      if (!updated) {
        notification.error('Failed to save campaign details');
        return;
      }

      notification.success('Campaign name and description saved');
    } finally {
      setSavingNameAndDescription(false);
    }
  };

  const updateCampaignStartDate = async (campaignStartDate: string) => {
    if (isInitializing) return;

    setLoading(true);
    try {
      const browserLocalTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const startDateTz = campaignAutomation.contactWindowId
        ? browserLocalTz
        : campaignAutomation.schedule?.timeZoneOption.value;

      if (!startDateTz) {
        notification.error('Unable to determine campaign timezone');
        return;
      }

      const nextAutomation = {
        ...campaignAutomation,
        campaignStartDate,
        campaignStartDateTz: startDateTz,
      };
      setCampaignAutomation(nextAutomation);

      const saved = await saveCampaignAutomation(nextAutomation);
      if (!saved) {
        notification.error('Failed to update start date');
        return;
      }

      const activated = await activateCampaign();
      if (!activated) {
        // This is less confusing than saying "Failed to activate campaign"
        notification.error('Failed to update start date');
        return;
      }

      setShowScheduleInput(false);
      notification.success('Campaign start date updated');
    } catch {
      notification.error('Failed to update campaign schedule');
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    if (!campaignReferenceId) return;

    setLoadingContacts(true);
    try {
      const queryObject: QueryObject = {
        page: '1',
        size: '1',
        filters: JSON.stringify({
          workflowReferenceId: campaignReferenceId,
        }),
        sort_column: 'thread_created_at',
        sort_desc: 'true',
      };

      const query = new URLSearchParams(queryObject as Record<string, string>);
      const contacts: PaginatedResponse<WorkflowExecutionThreadWithComputedStatus> =
        await get(`/workflow_threads?${query.toString()}`);

      setCampaignMetrics((prev) => ({
        ...prev,
        totalContacts: contacts.total,
      }));
    } catch {
      notification.error('Failed to fetch contacts');
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleStartCampaign = async () => {
    if (isInitializing) return;

    setLoading(true);
    try {
      const browserLocalTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const startDateTz = campaignAutomation.contactWindowId
        ? browserLocalTz
        : campaignAutomation.schedule?.timeZoneOption.value;
      if (!startDateTz) {
        notification.error('Unable to determine campaign timezone');
        return;
      }
      const startDate = isoStringToDate(new Date().toISOString(), startDateTz);
      const nextAutomation = {
        ...campaignAutomation,
        campaignStartDate: startDate,
        campaignStartDateTz: startDateTz,
      };
      setCampaignAutomation(nextAutomation);

      const saved = await saveCampaignAutomation(nextAutomation);
      if (!saved) {
        notification.error('Failed to start campaign');
        return;
      }

      const activated = await activateCampaign();
      if (!activated) {
        // This is less confusing than saying "Failed to activate campaign"
        notification.error('Failed to start campaign');
        return;
      }
      notification.success('Campaign started');
      setShowScheduleInput(false);
    } catch {
      notification.error('Failed to start campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleStopCampaign = async () => {
    if (isInitializing) return;

    setLoading(true);
    try {
      const nextAutomation = {
        ...campaignAutomation,
        campaignStartDate: undefined,
        campaignStartDateTz: undefined,
      };
      setCampaignAutomation(nextAutomation);

      const saved = await saveCampaignAutomation(nextAutomation);
      if (!saved) {
        notification.error('Failed to stop campaign');
        return;
      }

      const stopped = await deactivateCampaign();
      if (!stopped) {
        notification.error('Failed to stop campaign');
        return;
      }

      notification.success('Campaign stopped');
      setCampaignStatus(CampaignBadgeStatus.STOPPED);
      setShowScheduleInput(true);
    } catch {
      notification.error('Failed to stop campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!campaignReferenceId) return;

    setLoading(true);

    try {
      await campaignsService.deleteCampaign(campaignReferenceId);
      navigate('/beta/campaigns');
      notification.success('Campaign deleted');
    } catch {
      notification.error('Failed to delete campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelScheduledStart = async () => {
    if (isInitializing) return;

    setLoading(true);
    try {
      const nextAutomation = {
        ...campaignAutomation,
        campaignStartDate: undefined,
        campaignStartDateTz: undefined,
      };
      setCampaignAutomation(nextAutomation);

      const saved = await saveCampaignAutomation(nextAutomation);
      if (!saved) {
        notification.error('Failed to cancel scheduled start');
        return;
      }

      const stopped = await deactivateCampaign();
      if (!stopped) {
        notification.error('Failed to cancel scheduled start');
        return;
      }

      notification.success('Campaign scheduled start cancelled');
      setCampaignStatus(CampaignBadgeStatus.STOPPED);
      setShowScheduleInput(false);
    } catch {
      notification.error('Failed to cancel scheduled start');
    } finally {
      setLoading(false);
    }
  };
  // Effects
  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignReferenceId]);

  useEffect(() => {
    if (isInitializing) return;

    syncCampaignStatusFromAutomation();
  }, [isInitializing, syncCampaignStatusFromAutomation]);

  const handleSave = async (exit: boolean = false) => {
    setLoading(true);
    try {
      const campaignSaved = await saveCampaignAutomation();
      if (campaignSaved) {
        notification.success('Campaign saved successfully');
        setIsEditingCampaignDetails(false);
        if (exit) {
          navigate('/beta/campaigns');
        }
      }
      fetchContacts();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const workflowStats = workflowStatsData?.getWorkflowStats ?? [];

    const queued =
      (workflowStats.find(
        (stat) => stat.status === WorkflowExecutionThreadStatus.PAUSED,
      )?.count || 0) +
      (workflowStats.find(
        (stat) => stat.status === WorkflowExecutionThreadStatus.UNSCHEDULED,
      )?.count || 0);

    const active =
      (workflowStats.find(
        (stat) => stat.status === WorkflowExecutionThreadStatus.RUNNING,
      )?.count || 0) +
      (workflowStats.find(
        (stat) => stat.status === WorkflowExecutionThreadStatus.RATE_LIMITED,
      )?.count || 0);

    const errored =
      workflowStats.find(
        (stat) => stat.status === WorkflowExecutionThreadStatus.ERRORED,
      )?.count || 0;

    const exited =
      workflowStats.find(
        (stat) => stat.status === WorkflowExecutionThreadStatus.COMPLETED,
      )?.count || 0;

    setCampaignMetrics((prev) => ({
      ...prev,
      queued,
      active,
      errored,
      exited,
    }));
  }, [workflowStatsData]);

  // Computed values
  const moreOptionsItems = ['Export Calls CSV', 'Duplicate Campaign'];
  const displayedStartDate =
    campaignAutomation.status === WorkflowStatus.WORKFLOW_PAUSED
      ? undefined
      : campaignAutomation.campaignStartDate;
  const displayedStartDateTz =
    campaignAutomation.status === WorkflowStatus.WORKFLOW_PAUSED
      ? undefined
      : campaignAutomation.campaignStartDateTz;

  const tabItems: Array<{
    tab: CampaignOverviewTab;
    label: string;
    content: React.ReactNode;
  }> = [
    {
      tab: CampaignOverviewTab.OVERVIEW,
      label: 'Overview',
      content: (
        <div className="p-4 space-y-6">
          <CampaignAnalyticsSection
            metrics={campaignMetrics}
            startDate={displayedStartDate}
            startDateTz={displayedStartDateTz}
            loadingContacts={loadingContacts}
            workflowStatsLoading={workflowStatsLoading}
          />
        </div>
      ),
    },
    {
      tab: CampaignOverviewTab.COMMUNICATION,
      label: 'Communication',
      content: <CommunicationContent disabled={!isInactive} />,
    },
    {
      tab: CampaignOverviewTab.CONTACTS,
      label: 'Contacts',
      content: <ContactsContent onEditCrmIntegration={() => goToSettings()} />,
    },
    {
      tab: CampaignOverviewTab.ACTIVITY,
      label: 'Activity',
      content: <ActivityContent />,
    },
    {
      tab: CampaignOverviewTab.SETTINGS,
      label: 'Settings',
      content: <SettingsContent />,
    },
  ];

  const handleTabChange = (index: number) => {
    const nextTab = tabItems[index]?.tab;
    if (nextTab) {
      setSearchParams({ tab: nextTab });
    }
  };

  const goToSettings = () => {
    const settingsTabIndex = tabItems.findIndex(
      (item) => item.tab === CampaignOverviewTab.SETTINGS,
    );
    if (settingsTabIndex !== -1) handleTabChange(settingsTabIndex);
  };

  const selectedTabKey = (currentTabParam || CampaignOverviewTab.OVERVIEW)
    .toLowerCase()
    .trim();
  const selectedTabIndex = tabItems.findIndex(
    (item) => item.tab === selectedTabKey,
  );
  const selectedIndex = selectedTabIndex === -1 ? 0 : selectedTabIndex;

  useEffect(() => {
    setCanStartCampaign(
      campaignMetrics.totalContacts > 0 ||
        Boolean(campaignAutomation.crmProviderOption.value),
    );
  }, [
    campaignAutomation.crmProviderOption.value,
    campaignMetrics.totalContacts,
  ]);

  const getStartCampaignTooltipMessage = useCallback((action: string) => {
    return `You need to have at least one contact in your campaign or a CRM configured to ${action} a campaign.`;
  }, []);

  if (loading || isInitializing || !campaignAutomation.referenceId)
    return <CampaignOverviewSkeleton />;

  return (
    <>
      <Heading
        className="border-b-0"
        fixedHeader={true}
        title={
          <div>
            <div className="flex items-start gap-2">
              <EditableTitle
                onSave={saveNameAndDescription}
                titleInputRef={titleInputRef}
                metadata=""
                titlePlaceholder="Campaign Name"
                subtitlePlaceholder="Add a description"
                onNameChange={handleNameChange}
                onDescriptionChange={handleDescriptionChange}
                loading={loading || savingNameAndDescription}
                isEditing={isEditingCampaignDetails}
                setIsEditing={setIsEditingCampaignDetails}
                name={campaignAutomation.name || 'Untitled Campaign'}
                description={campaignAutomation.description || ''}
                containerClassName={clsx(
                  savingNameAndDescription && 'opacity-50',
                )}
              />
              <CampaignBadge status={campaignStatus} className="ml-4" />
            </div>
            <div className="flex items-start gap-2">
              <div className="text-neutral-600 italic">
                ID: {campaignReferenceId}
              </div>
            </div>
          </div>
        }
      >
        <div className="flex flex-col gap-2 mt-3 items-center md:flex-row md:gap-2">
          {/* TODO: Add back in when we have more options */}
          <div className="relative">
            <Button
              variant="text"
              size="small"
              onClick={() =>
                setIsMoreOptionsDropdownOpen(!isMoreOptionsDropdownOpen)
              }
            >
              <DotsThree size={24} />
            </Button>
            <DropdownMenu
              isOpen={isMoreOptionsDropdownOpen}
              position="bottom-right"
              onItemClick={handleMoreOptionsClick}
              className="min-w-[200px]"
              items={moreOptionsItems}
            />
          </div>

          {isInactive && (
            <Button
              variant="outlined"
              size="small"
              className="text-error border-error/20 hover:bg-error/5"
              onClick={() => setDeleteCampaignModalOpen(true)}
            >
              <TrashSimple size={16} className="mr-2" />
              Delete Campaign
            </Button>
          )}

          {canSeeScheduleInput && (
            <Popover className="relative">
              {({ open }) => (
                <TooltipComponent
                  position="bottom"
                  content={
                    !canStartCampaign
                      ? getStartCampaignTooltipMessage('schedule')
                      : ''
                  }
                >
                  <Popover.Button as={Fragment}>
                    <Button
                      variant="outlined"
                      size="small"
                      className={open ? 'ring-2 ring-primary/20' : ''}
                      disabled={!canStartCampaign}
                    >
                      <CalendarBlank size={16} className="mr-2" />
                      Schedule Start
                    </Button>
                  </Popover.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="absolute right-0 z-10 mt-1 w-56">
                      <div className="bg-neutral-0 shadow-lg rounded-md py-1 overflow-auto focus:outline-none text-sm border border-neutral-100">
                        <div className="px-3 py-2.5 gap-2 flex flex-col">
                          <Input
                            id="campaign-start-date"
                            label="Campaign Start Date"
                            type="date"
                            value={startDateDraft}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => setStartDateDraft(e.target.value)}
                            className="max-w-xs"
                            required={true}
                          />

                          <Button
                            variant="outlined"
                            size="small"
                            disabled={
                              isInitializing ||
                              !startDateDraft ||
                              startDateDraft.trim() === ''
                            }
                            onClick={() => {
                              updateCampaignStartDate(startDateDraft);
                            }}
                          >
                            Set Schedule
                          </Button>
                        </div>
                      </div>
                    </Popover.Panel>
                  </Transition>
                </TooltipComponent>
              )}
            </Popover>
          )}

          {campaignStatus === CampaignBadgeStatus.RUNNING && (
            <Button
              variant="outlined"
              size="small"
              className="text-error border-error/20 hover:bg-error/5"
              onClick={handleStopCampaign}
            >
              <StopCircle size={16} className="mr-2" />
              Stop
            </Button>
          )}

          {isInactive && (
            <TooltipComponent
              position="bottom"
              content={
                !canStartCampaign ? getStartCampaignTooltipMessage('start') : ''
              }
            >
              <Button
                size="small"
                onClick={handleStartCampaign}
                disabled={!canStartCampaign}
              >
                <PlayCircle size={16} className="mr-2" />
                Start
              </Button>
            </TooltipComponent>
          )}

          {campaignStatus === CampaignBadgeStatus.SCHEDULED && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => handleCancelScheduledStart()}
            >
              <CalendarX size={16} className="mr-2" />
              Cancel Scheduled Start
            </Button>
          )}

          <div
            className="mx-1 h-4 w-px bg-neutral-200 select-none pointer-events-none hidden md:block"
            aria-hidden="true"
          ></div>

          <Button
            size="small"
            variant="outlined"
            onClick={() => handleSave(true)}
          >
            <ArrowUUpLeft size={16} className="mr-2" />
            Save and exit
          </Button>

          <Button size="small" onClick={() => handleSave(false)}>
            <FloppyDisk size={16} className="mr-2" />
            Save
          </Button>
        </div>
      </Heading>

      <ContentWrapper className="p-0 h-auto">
        <Tab
          items={tabItems}
          selectedIndex={selectedIndex}
          onChange={handleTabChange}
        />
      </ContentWrapper>

      <Modal
        title={`Delete Campaign: ${campaignAutomation.name || ''}`}
        isOpen={deleteCampaignModalOpen}
        onClose={() => {
          setDeleteCampaignModalOpen(false);
        }}
        actionButton={
          <Button onClick={handleDeleteCampaign}>Delete Campaign</Button>
        }
        className="w-auto max-w-md"
      >
        <div className="text-center">
          Are you sure you want to delete this campaign?
        </div>
      </Modal>
    </>
  );
};

const CampaignOverviewWithWizard = () => {
  const { id: campaignReferenceId } = useParams();

  return (
    <WizardProvider
      key={campaignReferenceId}
      isEditing
      editReferenceId={campaignReferenceId}
    >
      <CampaignOverview campaignReferenceId={campaignReferenceId!} />
    </WizardProvider>
  );
};

export default withIntegrationApp(CampaignOverviewWithWizard);
